import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../../config';
import './PortfolioDashboard.css';


const ExpensesVendorsScreen = ({ dateRange: propDateRange, propertyId: propPropertyId }) => {
  const navigate = useNavigate();
  const [activeView, setActiveView] = useState('expenses'); // 'expenses' or 'vendors'
  const [dateRange, setDateRange] = useState(propDateRange || {
    start_date: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    end_date: new Date().toISOString().split('T')[0]
  });
  const [filters, setFilters] = useState({
    property_id: propPropertyId || null,
    vendor_id: null,
    category: 'all',
    status: 'all'
  });

  // Sync props to state
  useEffect(() => {
    if (propDateRange) setDateRange(propDateRange);
    if (propPropertyId !== undefined) setFilters(prev => ({ ...prev, property_id: propPropertyId }));
  }, [propDateRange, propPropertyId]);
  const [expenses, setExpenses] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showVendorModal, setShowVendorModal] = useState(false);
  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [newVendor, setNewVendor] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    categories: '',
    default_category: ''
  });
  const [newExpense, setNewExpense] = useState({
    property_id: '',
    unit_id: '',
    vendor_id: '',
    category: '',
    amount: '',
    expense_date: new Date().toISOString().split('T')[0],
    description: '',
    payment_status: 'unpaid'
  });

  useEffect(() => {
    fetchProperties();
    fetchData();
  }, [activeView, dateRange, filters]);

  const fetchProperties = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/properties`);
      if (response.ok) {
        const data = await response.json();
        setProperties(data);
      }
    } catch (err) {
      console.error('Error fetching properties:', err);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append('start_date', dateRange.start_date);
      params.append('end_date', dateRange.end_date);
      if (filters.property_id) params.append('property_id', filters.property_id);
      if (filters.vendor_id) params.append('vendor_id', filters.vendor_id);
      if (filters.category !== 'all') params.append('category', filters.category);
      if (filters.status !== 'all') params.append('status', filters.status);

      if (activeView === 'expenses') {
        const response = await fetch(`${API_BASE_URL}/api/financial/expenses?${params}`);
        if (response.ok) {
          const data = await response.json();
          setExpenses(data);
        }
      } else if (activeView === 'vendors') {
        const response = await fetch(`${API_BASE_URL}/api/vendors`);
        if (response.ok) {
          const data = await response.json();
          setVendors(data);
        }
      }
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString();
  };

  // Calculate expense metrics
  const expenseMetrics = expenses.reduce((acc, exp) => {
    acc.total += exp.amount || 0;
    if (exp.vendor_name) {
      acc.vendors[exp.vendor_name] = (acc.vendors[exp.vendor_name] || 0) + (exp.amount || 0);
    }
    if (exp.expense_category) {
      acc.categories[exp.expense_category] = (acc.categories[exp.expense_category] || 0) + (exp.amount || 0);
    }
    return acc;
  }, { total: 0, vendors: {}, categories: {} });

  const topVendors = Object.entries(expenseMetrics.vendors)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5)
    .map(([name, amount]) => ({ name, amount }));

  const categoryBreakdown = Object.entries(expenseMetrics.categories)
    .sort((a, b) => b[1] - a[1])
    .map(([category, amount]) => ({ category, amount }));

  return (
    <div className="expenses-vendors-screen">
      {/* Header */}
      <div className="screen-header">
        <h1>Expenses & Vendors</h1>
        <div className="header-actions">
          {activeView === 'expenses' && (
            <button onClick={() => setShowExpenseModal(true)} className="export-btn">
              + Add Expense
            </button>
          )}
          {activeView === 'vendors' && (
            <button onClick={() => {
              setSelectedVendor(null);
              setNewVendor({
                name: '',
                email: '',
                phone: '',
                address: '',
                categories: '',
                default_category: ''
              });
              setShowVendorModal(true);
            }} className="export-btn">
              + Add Vendor
            </button>
          )}
        </div>
      </div>

      {/* Metrics */}
      {activeView === 'expenses' && (
        <div className="metrics-section">
          <div className="metric-card">
            <div className="metric-label">Total Expenses</div>
            <div className="metric-value">{formatCurrency(expenseMetrics.total)}</div>
          </div>
          <div className="metric-card">
            <div className="metric-label">Top Vendor</div>
            <div className="metric-value">
              {topVendors[0] ? `${topVendors[0].name}: ${formatCurrency(topVendors[0].amount)}` : 'N/A'}
            </div>
          </div>
          <div className="metric-card">
            <div className="metric-label">Categories</div>
            <div className="metric-value">{categoryBreakdown.length} categories</div>
          </div>
        </div>
      )}

      {/* View Toggle */}
      <div className="view-tabs">
        <button
          className={activeView === 'expenses' ? 'active' : ''}
          onClick={() => setActiveView('expenses')}
        >
          Expenses
        </button>
        <button
          className={activeView === 'vendors' ? 'active' : ''}
          onClick={() => setActiveView('vendors')}
        >
          Vendors
        </button>
      </div>

      {/* Simplified Filters */}
      <div className="filters-section">
        {activeView === 'expenses' && (
          <>
            <div className="filter-group">
              <label>Category:</label>
              <select
                value={filters.category}
                onChange={(e) => setFilters({...filters, category: e.target.value})}
                className="filter-select"
              >
                <option value="all">All Categories</option>
                {Array.from(new Set(expenses.map(e => e.expense_category).filter(Boolean))).map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
            <div className="filter-group">
              <label>Status:</label>
              <select
                value={filters.status}
                onChange={(e) => setFilters({...filters, status: e.target.value})}
                className="filter-select"
              >
                <option value="all">All Statuses</option>
                <option value="unpaid">Unpaid</option>
                <option value="paid">Paid</option>
              </select>
            </div>
          </>
        )}
      </div>

      {/* Expenses View */}
      {activeView === 'expenses' && (
        <div className="expenses-content">
          {/* Top Vendors & Category Breakdown */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '24px' }}>
            <div className="dashboard-section">
              <h3>Top 5 Vendors</h3>
              {topVendors.length === 0 ? (
                <div className="empty-state">No vendor data</div>
              ) : (
                <div className="vendor-list">
                  {topVendors.map((vendor, idx) => (
                    <div key={idx} className="vendor-item">
                      <span>{vendor.name}</span>
                      <span style={{ fontWeight: 600 }}>{formatCurrency(vendor.amount)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="dashboard-section">
              <h3>Expense Breakdown by Category</h3>
              {categoryBreakdown.length === 0 ? (
                <div className="empty-state">No category data</div>
              ) : (
                <div className="category-list">
                  {categoryBreakdown.map((item, idx) => (
                    <div key={idx} className="category-item">
                      <span>{item.category}</span>
                      <span style={{ fontWeight: 600 }}>{formatCurrency(item.amount)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Expenses Table */}
          <div className="dashboard-section">
            <h2>Expenses</h2>
            {loading ? (
              <div className="loading-container">
                <div className="spinner"></div>
              </div>
            ) : (
              <div className="table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Vendor</th>
                      <th>Property</th>
                      <th>Category</th>
                      <th>Description</th>
                      <th>Amount</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {expenses.map(exp => (
                      <tr 
                        key={exp.id}
                        onClick={() => exp.property_id && navigate(`/portfolio/property/${exp.property_id}`)}
                        className={exp.property_id ? "clickable-row" : ""}
                      >
                        <td>{formatDate(exp.expense_date)}</td>
                        <td>{exp.vendor_name || '-'}</td>
                        <td>
                          {properties.find(p => p.id === exp.property_id)?.property_name || 
                           properties.find(p => p.id === exp.property_id)?.name || '-'}
                        </td>
                        <td>{exp.expense_category || '-'}</td>
                        <td>{exp.description || '-'}</td>
                        <td>{formatCurrency(exp.amount)}</td>
                        <td>
                          <span style={{
                            padding: '4px 12px',
                            borderRadius: '12px',
                            fontSize: '12px',
                            fontWeight: 600,
                            backgroundColor: exp.payment_status === 'paid' ? '#d4edda' : '#fff3cd',
                            color: exp.payment_status === 'paid' ? '#155724' : '#856404'
                          }}>
                            {exp.payment_status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Vendors View */}
      {activeView === 'vendors' && (
        <div className="vendors-content">
          <div className="dashboard-section">
            <h2>Vendors</h2>
            {loading ? (
              <div className="loading-container">
                <div className="spinner"></div>
              </div>
            ) : (
              <div className="vendor-grid">
                {vendors.map(vendor => (
                  <div key={vendor.id} className="vendor-card">
                    <div className="vendor-card-header">
                      <h3>{vendor.name}</h3>
                      <button 
                        onClick={() => {
                          setSelectedVendor(vendor);
                          setNewVendor({
                            name: vendor.name,
                            email: vendor.email || '',
                            phone: vendor.phone || '',
                            address: vendor.address || '',
                            categories: vendor.categories || '',
                            default_category: vendor.default_category || ''
                          });
                          setShowVendorModal(true);
                        }}
                        className="edit-btn"
                      >
                        Edit
                      </button>
                    </div>
                    <div className="vendor-card-body">
                      {vendor.email && <div className="vendor-info">📧 {vendor.email}</div>}
                      {vendor.phone && <div className="vendor-info">📞 {vendor.phone}</div>}
                      {vendor.categories && (
                        <div className="vendor-info">
                          <strong>Categories:</strong> {vendor.categories}
                        </div>
                      )}
                      {vendor.default_category && (
                        <div className="vendor-info">
                          <strong>Default:</strong> {vendor.default_category}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Vendor Modal */}
      {showVendorModal && (
        <div className="modal-overlay" onClick={() => setShowVendorModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <div className="modal-header">
              <h2>{selectedVendor ? 'Edit Vendor' : 'Add Vendor'}</h2>
              <button className="modal-close" onClick={() => setShowVendorModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-grid">
                <div className="form-field">
                  <label>Vendor Name *</label>
                  <input
                    type="text"
                    value={newVendor.name}
                    onChange={(e) => setNewVendor({...newVendor, name: e.target.value})}
                    className="form-input"
                  />
                </div>
                <div className="form-field">
                  <label>Email</label>
                  <input
                    type="email"
                    value={newVendor.email}
                    onChange={(e) => setNewVendor({...newVendor, email: e.target.value})}
                    className="form-input"
                  />
                </div>
                <div className="form-field">
                  <label>Phone</label>
                  <input
                    type="tel"
                    value={newVendor.phone}
                    onChange={(e) => setNewVendor({...newVendor, phone: e.target.value})}
                    className="form-input"
                  />
                </div>
                <div className="form-field">
                  <label>Address</label>
                  <input
                    type="text"
                    value={newVendor.address}
                    onChange={(e) => setNewVendor({...newVendor, address: e.target.value})}
                    className="form-input"
                  />
                </div>
                <div className="form-field">
                  <label>Categories (comma-separated)</label>
                  <input
                    type="text"
                    value={newVendor.categories}
                    onChange={(e) => setNewVendor({...newVendor, categories: e.target.value})}
                    className="form-input"
                    placeholder="e.g., Plumbing, HVAC, Landscaping"
                  />
                </div>
                <div className="form-field">
                  <label>Default Category</label>
                  <input
                    type="text"
                    value={newVendor.default_category}
                    onChange={(e) => setNewVendor({...newVendor, default_category: e.target.value})}
                    className="form-input"
                  />
                </div>
              </div>
              <div className="modal-actions">
                <button 
                  className="save-button"
                  onClick={async () => {
                    try {
                      const url = selectedVendor 
                        ? `${API_BASE_URL}/api/vendors/${selectedVendor.id}`
                        : `${API_BASE_URL}/api/vendors`;
                      const method = selectedVendor ? 'PUT' : 'POST';
                      
                      const response = await fetch(url, {
                        method,
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(newVendor)
                      });
                      
                      if (response.ok) {
                        setShowVendorModal(false);
                        fetchData(); // Refresh vendor list
                      } else {
                        alert('Error saving vendor');
                      }
                    } catch (err) {
                      console.error('Error saving vendor:', err);
                      alert('Error saving vendor');
                    }
                  }}
                >
                  Save
                </button>
                <button className="cancel-button" onClick={() => setShowVendorModal(false)}>Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExpensesVendorsScreen;

